import React, { useMemo, useState, useCallback, useEffect } from "react";

import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  TextField,
  Chip,
  Slider,
  CircularProgress,
  Grid,
  Tooltip,
  Alert,
} from "@mui/material";
import { CheckCircle, Cancel, Error } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { scoreLevels } from "../../../../context/global";
import { EuroFormat, getTotalValueFromSegments, NumberFormat, prettifyNumber } from "../../../../functions/Formatters";
import { updateLoopDesignScores, ensureDesignScores } from "../DesignActions";
import QuantKPIInputSpreadsheet from "../../../CustomComponents/InputFields/QuantKPIInputSpreadsheet";
import FormattedNumberInput from "../../../CustomComponents/InputFields/FormattedNumberInput";
import KPITypeChip from "../../../CustomComponents/KPITypeChip";

export default function DesignScoreComponent({ project, design, designScores, phaseID, loopID, editable }) {
  const dispatch = useDispatch();

  const [scoreValues, setScoreValues] = useState({});
  const [scoreComments, setScoreComments] = useState({});
  const [savingStates, setSavingStates] = useState({});
  const [creatingScores, setCreatingScores] = useState(false);
  const { flatKpis, flatThemes } = useSelector((state) => state.themes);

  // Derive the loop object and its loop_kpis
  const loopObj = useMemo(() => {
    for (const phase of project.phases || []) {
      const found = (phase.loops || []).find((l) => l.id === loopID);
      if (found) return found;
    }
    return null;
  }, [project, loopID]);
  

  const referenceDesign = loopObj?.reference_design || (design.project === null ? project.reference_design : undefined);
  const loopKpis = [...(loopObj?.loop_kpis || [])].filter((kpi) => kpi.is_selected === true).sort((a, b) => flatKpis[a.kpi].name.localeCompare(flatKpis[b.kpi].name));

  // Group KPIs by theme
  const groupedKPIs = useMemo(() => {
    const groups = {};
    
    // Group KPIs by theme
    loopKpis.forEach(loopKPI => {
      const kpi = flatKpis[loopKPI.kpi];
      const themeInfo = flatThemes[kpi?.theme];
      if (themeInfo) {
        const themeKey = `${themeInfo.supertheme}-${themeInfo.name}`;
        if (!groups[themeKey]) {
          groups[themeKey] = {
            theme: themeInfo,
            kpis: []
          };
        }
        groups[themeKey].kpis.push(loopKPI);
      }
    });
    
    // Sort groups by supertheme, then by theme name
    const sortedGroups = Object.values(groups).sort((a, b) => {
      const superthemeComparison = a.theme.supertheme - b.theme.supertheme;
      if (superthemeComparison !== 0) {
        return superthemeComparison;
      }
      return a.theme.name.localeCompare(b.theme.name);
    });
    
    // Sort KPIs within each group
    sortedGroups.forEach(group => {
      group.kpis.sort((a, b) => flatKpis[a.kpi].name.localeCompare(flatKpis[b.kpi].name));
    });
    
    return sortedGroups;
  }, [loopKpis, flatKpis, flatThemes]);

  // Function to handle missing design scores
  const handleMissingScores = useCallback(async () => {
    if (!design || !loopKpis.length || creatingScores) return;

    // Check if there are missing scores
    const missingScores = loopKpis.filter(loopKpi => 
      !designScores.find(score => score.loop_kpi === loopKpi.id)
    );

    if (missingScores.length > 0) {
      console.log(`Creating ${missingScores.length} missing design scores for design ${design.name}`);
      setCreatingScores(true);
      try {
        await ensureDesignScores(design.id, phaseID, loopID, dispatch);
        console.log(`Successfully created missing design scores for design ${design.name}`);
      } catch (error) {
        console.error("Failed to create missing design scores:", error);
      } finally {
        setCreatingScores(false);
      }
    }
  }, [design, loopKpis, designScores, phaseID, loopID, dispatch, creatingScores]);

  // Check for missing scores when component mounts or dependencies change
  useEffect(() => {
    handleMissingScores();
  }, [design.id]);

  // Initialize score values and comments from existing scores
  useEffect(() => {
    const initialValues = {};
    const initialComments = {};

    designScores.forEach((score) => {
      initialValues[score.id] = score.value || "";
      initialComments[score.id] = score.comment || "";
    });

    setScoreValues(initialValues);
    setScoreComments(initialComments);
  }, [designScores]);

  const getKPITypeLabel = useCallback((scaleType) => {
    switch (scaleType) {
      case 0:
        return "Score";
      case 1:
        return "Segment";
      case 2:
        return "Global";
      default:
        return "Onbekend";
    }
  }, []);

  const getKPITypeColor = useCallback((scaleType) => {
    switch (scaleType) {
      case 0:
        return "primary";
      case 1:
        return "secondary";
      case 2:
        return "success";
      default:
        return "default";
    }
  }, []);

  const handleScoreValueChange = useCallback((scoreId, value) => {
    setScoreValues((prev) => ({
      ...prev,
      [scoreId]: value,
    }));
  }, []);

  const handleScoreCommentChange = useCallback((scoreId, comment) => {
    setScoreComments((prev) => ({
      ...prev,
      [scoreId]: comment,
    }));
  }, []);

  const handleSaveScore = useCallback(
    async (scoreId, newValue = undefined) => {
      if (!editable) return;

      setSavingStates((prev) => ({ ...prev, [scoreId]: true }));

      try {
        const value = newValue !== undefined ? newValue : scoreValues[scoreId];
        const comment = scoreComments[scoreId];

        const scores_to_update = {
          [scoreId]: {
            value: value === "" ? null : value,
            comment: comment || "",
          },
        };

        updateLoopDesignScores(scores_to_update, phaseID, loopID, dispatch);
      } catch (error) {
        console.error("Error saving score:", error);
      } finally {
        setSavingStates((prev) => ({ ...prev, [scoreId]: false }));
      }
    },
    [editable, scoreValues, scoreComments, phaseID, loopID, dispatch]
  );

  const handleSetSimplifiedMode = useCallback(async (scoreId, useSimplified, newValue) => {
    if (!editable) return;

    setSavingStates((prev) => ({ ...prev, [scoreId]: true }));

    try {
      const scores_to_update = {
        [scoreId]: {
          use_simplified: useSimplified,
          value: newValue,
        },
      };

      updateLoopDesignScores(scores_to_update, phaseID, loopID, dispatch);
    } catch (error) {
      console.error("Error setting simplified mode:", error);
    } finally {
      setSavingStates((prev) => ({ ...prev, [scoreId]: false }));
    }
  }, [editable, phaseID, loopID, dispatch]);

  const iconSwitch = useCallback((state) => {
    switch (state) {
      case "saving":
        return <CircularProgress size={16} />;
      case "saved":
        return <CheckCircle color="success" />;
      case "changed":
        return <Error color="default" />;
      case "error":
        return <Cancel color="error" />;
      default:
        return <Cancel color="disabled" />;
    }
  }, []);

  const formatGoalValue = useCallback((kpi, loopGoal) => {
    if (kpi?.scale_type === 0) {
      return scoreLevels[(loopGoal.value || 0) - 1]?.label;
    }

    const operator = kpi?.maximize ? "≥" : "≤";
    const formattedValue =
      kpi?.unit === "€" ? EuroFormat(loopGoal.value) : `${NumberFormat(loopGoal.value)} ${kpi?.unit || ""}`;
    return `${operator} ${formattedValue}`;
  }, []);

  
  return (
    
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: creatingScores ? 2 : 0 }}>
        <Typography>
          {creatingScores && (
            <Box component="span" sx={{ ml: 2, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Ontbrekende scores worden aangemaakt...
              </Typography>
            </Box>
          )}
        </Typography>
      </Box>
      {groupedKPIs.length > 0 ? (
        <Box>
          {groupedKPIs.map((group) => (
            <Box key={`${group.theme.supertheme}-${group.theme.name}`} sx={{ mb: 3 }}>
              {/* Theme Header */}
              <Box sx={{ mb: 1 }}>
                <Tooltip 
                  title={group.theme.description || "Geen beschrijving beschikbaar"} 
                  placement="top"
                  arrow
                >
                  <Typography 
                    variant="h6" 
                    component="h3"
                    sx={{ 
                      fontWeight: 'bold',
                      color: 'text.primary',
                      cursor: 'help',
                      borderBottom: 2,
                      borderColor: 'primary.main',
                      pb: 0.5,
                      display: 'inline-block'
                    }}
                  >
                    {group.theme.name}
                  </Typography>
                </Tooltip>
              </Box>
              
              {/* KPIs under this theme */}
              <List sx={{ width: "100%", bgcolor: "background.paper", borderRadius: 1 }}>
                {group.kpis.map((loopKpi, kpiIndex) => {
            const kpi = flatKpis[loopKpi.kpi];
            const loopGoal = loopKpi.loop_goals?.[0];

            // Find the design score for this loop_kpi
            const designScore = designScores.find((score) => score.loop_kpi === loopKpi.id);
            
            // If no design score exists, show loading state while creating scores
            if (!designScore) {
              if (creatingScores) {
                return (
                  <Box key={`kpi_${loopKpi.id}`}>
                    <ListItem sx={{ flexDirection: "column", alignItems: "stretch" }}>
                      <Box display="flex" alignItems="center" width="100%" gap={2}>
                        <CircularProgress size={20} />
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight="medium">
                              {kpi?.name || "Onbekende KPI"} - Score wordt aangemaakt...
                            </Typography>
                          }
                        />
                      </Box>
                    </ListItem>
                    <Divider />
                  </Box>
                );
              }
              // If not creating scores, skip this KPI (it should be created on next render)
              
              return <Typography key={`kpi_${loopKpi.id}`}>LoopKPI {loopKpi.id} not found</Typography>;
            }

            const currentValue = scoreValues[designScore.id] ?? designScore.value;
            const totalValue =
              kpi?.scale_type === 0 || kpi?.scale_type === 2
                ? currentValue
                : getTotalValueFromSegments(currentValue);

            const currentComment = scoreComments[designScore.id];
            const hasScore = currentValue !== null && currentValue !== undefined && currentValue !== "";
            const hasChanged = designScore.value != currentValue;
            const isSaving = savingStates[designScore.id] || false;

            const state = isSaving ? "saving" : !hasScore ? "error" :hasChanged ? "changed" : "saved";

            return (
              <Box key={`kpi_${loopKpi.id}`}>
                <ListItem sx={{ flexDirection: "column", alignItems: "stretch" }}>
                  <Box display="flex" alignItems="center" width="100%">
                    <ListItemIcon>{iconSwitch(state)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight="medium">
                            {kpi?.name || "Onbekende KPI"}
                          </Typography>
                          {kpi?.unit && <Chip label={kpi.unit} size="small" variant="outlined" />}
                          <KPITypeChip scaleType={kpi?.scale_type} />
                        </Box>
                      }
                      secondary={
                        <Box>
                          {kpi?.description && (
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              {kpi?.description}
                            </Typography>
                          )}
                          {/* Goal Info - always displayed */}
                          {loopGoal && !design.is_reference && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              Doel: {formatGoalValue(kpi, loopGoal)}
                            </Typography>
                          )}
                          {!loopGoal && !design.is_reference && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            Geen doel ingesteld
                          </Typography>
                          )}

                          {/* Reference Score - always displayed */}
                          {!design.is_reference && referenceDesign && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              Referentie:{" "}
                              {kpi?.scale_type === 0
                                ? scoreLevels[referenceDesign.scores.find((s) => s.kpi === kpi.id)?.value - 1]?.label
                                : kpi?.scale_type === 1
                                ? prettifyNumber(
                                    getTotalValueFromSegments(
                                      referenceDesign.scores.find((s) => s.kpi === kpi.id)?.value,
                                      kpi.name
                                    ),
                                    kpi?.unit
                                  )
                                : prettifyNumber(
                                    referenceDesign.scores.find((s) => s.kpi === kpi.id)?.value,
                                    kpi?.unit
                                  )}
                            </Typography>
                          )}

                          {/* Total Value - always displayed, only for quantitative detailed KPIs */}
                          {kpi?.scale_type === 1 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight="bold"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              Score totaal:{" "}
                              {prettifyNumber(totalValue, kpi?.unit)}
                            </Typography>
                          )}

                          {/* Current Value, display when not editable*/}
                          <Box>
                            {!editable && (kpi?.scale_type === 0 || kpi?.scale_type === 2) && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                fontWeight="bold"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                Score:{" "}
                                {totalValue === null || totalValue === undefined || totalValue === ""
                                  ? "Geen score ingevuld"
                                  : kpi?.scale_type === 0
                                  ? scoreLevels[totalValue - 1]?.label
                                  : prettifyNumber(totalValue, kpi?.unit)}
                              </Typography>
                            )}

                            {/* Opmerkingen, display when not editable*/}
                            {!editable && (
                              <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                {scoreComments[designScore.id] && `Opmerking: ${scoreComments[designScore.id]}`}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Input fields - show spreadsheet always, others only when editable */}
                  {kpi?.scale_type === 1 ? (
                    // Quantitative Detailed KPI (Spreadsheet) - Always visible
                    <Box>
                      <Grid container spacing={2}>
                        <QuantKPIInputSpreadsheet
                          field={{
                            name: `scoreValue_${designScore.id}`,
                            value: currentValue,
                          }}
                          form={{
                            setFieldValue: (name, value) => {
                              handleScoreValueChange(designScore.id, value);
                              handleSaveScore(designScore.id, value);
                            },
                          }}
                          onDraftChange={(value) => {
                            handleScoreValueChange(designScore.id, value);
                          }}
                          onBlurSave={() => {
                            if (editable) {
                              handleSaveScore(designScore.id);
                            }
                          }}
                          useSimplified={designScore.use_simplified}
                          setUseSimplified={(useSimplified, value) => {
                            handleSetSimplifiedMode(designScore.id, useSimplified, value);
                          }}
                          kpi={kpi}
                          segments={loopObj?.loop_segments || [{ id: "total", name: "Totaal" }]}
                          layers={loopObj?.loop_layers || []}
                          disabled={!editable || isSaving}
                          referenceScore={referenceDesign?.scores.find((s) => s.kpi === kpi.id)?.value}
                        />
                      </Grid>
                      {editable && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Opmerking (optioneel)
                          </Typography>
                          <TextField
                            value={currentComment || ""}
                            onChange={(e) => handleScoreCommentChange(designScore.id, e.target.value)}
                            disabled={!editable || isSaving}
                            size="small"
                            fullWidth
                            placeholder="Voeg een opmerking toe..."
                            multiline
                            minRows={1}
                            onBlur={() => handleSaveScore(designScore.id)}
                            sx={{ maxWidth: 600 }}
                          />
                        </Box>
                      )}
                    </Box>
                  ) : editable ? (
                    <Box>
                      {kpi?.scale_type === 0 ? (
                        // Qualitative KPI (Slider) - Comment below
                        <Box>
                          <Slider
                            min={1}
                            max={5}
                            step={1}
                            value={currentValue || 1}
                            onChange={(e, value) => {
                              handleScoreValueChange(designScore.id, value);
                              handleSaveScore(designScore.id, value);
                            }}
                            disabled={!editable || isSaving}
                            marks={scoreLevels}
                            sx={{ mx: 7, maxWidth: 600, mb: 2 }}
                          />
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              Opmerking (optioneel)
                            </Typography>
                            <TextField
                              value={currentComment || ""}
                              onChange={(e) => handleScoreCommentChange(designScore.id, e.target.value)}
                              disabled={!editable || isSaving}
                              size="small"
                              fullWidth
                              placeholder="Voeg een opmerking toe..."
                              multiline
                              minRows={1}
                              onBlur={() => handleSaveScore(designScore.id)}
                              sx={{ maxWidth: 400 }}
                            />
                          </Box>
                        </Box>
                      ) : (
                        // Quantitative Simple KPI (TextField) - Comment next to it
                        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              Score
                            </Typography>
                            <TextField
                              value={currentValue || ""}
                              onChange={(e) => handleScoreValueChange(designScore.id, e.target.value)}
                              disabled={!editable || isSaving}
                              size="small"
                              fullWidth
                              placeholder={`Voer waarde in${kpi?.unit ? ` in ${kpi.unit}` : ""}`}
                              InputProps={{
                                inputComponent: FormattedNumberInput,
                                inputProps: {
                                  name: `score-${designScore.id}`,
                                  unit: kpi?.unit || "",
                                },
                              }}
                              onBlur={() => hasChanged && handleSaveScore(designScore.id)}
                            />
                          </Box>

                          <Box flex={2}>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              Opmerking (optioneel)
                            </Typography>
                            <TextField
                              value={currentComment || ""}
                              onChange={(e) => handleScoreCommentChange(designScore.id, e.target.value)}
                              disabled={!editable || isSaving}
                              size="small"
                              fullWidth
                              placeholder="Voeg een opmerking toe..."
                              multiline
                              minRows={1}
                              onBlur={() => handleSaveScore(designScore.id)}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : null}
                </ListItem>
                      {kpiIndex < group.kpis.length - 1 && <Divider />}
                    </Box>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          Er zijn nog geen KPI's toegevoegd voor deze ontwerpronde. Voeg KPI's toe om scores toe te kunnen voegen.
        </Alert>
      )}
    </Box>
  );
}
